import React from 'react'
import {
  FaArrowRight,
  FaStar,
  FaClock,
  FaPlane,
  FaPassport,
  FaTag,
} from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { cmsApi } from '../../services/cms'
import { useCMS } from '../../hooks/useCMS'
import { isMatchingCountry, type CountrySelection } from '../../utils/countryFilter'

type CampaignPick = {
  id?: string
  slug?: string
  campaignType?: string
  sectionKey?: string
  title?: string
  name?: string
  type?: string
  subtitle?: string
  category?: string
  rating?: string | number
  badgeText?: string
  originalPrice?: string | number
  discountedPrice?: string | number
  duration?: string
  expiresOn?: string
  tags?: string[]
  highlights?: string[]
  description?: string
  buttonText?: string
  ctaText?: string
  referenceId?: string
  referenceName?: string
  ctaUrl?: string
  imageUrl?: string
  countries?: CountrySelection
  country?: string | null
  leadCountry?: string | null
  siteCountry?: string | null
  sourceCountry?: string | null
  market?: string | null
}

type RefRecord = {
  id?: string
  slug?: string
  name?: string
  title?: string
  countries?: CountrySelection
  country?: string | null
  leadCountry?: string | null
  siteCountry?: string | null
  sourceCountry?: string | null
  market?: string | null
}

const normalizeCampaignValue = (value?: string) =>
  (value || '').toLowerCase().replace(/[\s_-]+/g, '')

const isFeaturedPick = (pick: CampaignPick) => {
  const campaignType = normalizeCampaignValue(pick.campaignType)
  const sectionKey = normalizeCampaignValue(pick.sectionKey)
  return campaignType.includes('featured') || sectionKey.includes('featured')
}

const formatPrice = (value?: string | number) => {
  if (value === undefined || value === null || value === '') return ''
  return typeof value === 'number' ? value.toLocaleString() : value
}

const formatExpiryDate = (value?: string) => {
  if (!value) return ''
  const parsedDate = new Date(value)
  if (Number.isNaN(parsedDate.getTime())) return value
  return parsedDate.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

const getTagIcon = (tag: string) => {
  const normalizedTag = normalizeCampaignValue(tag)
  if (normalizedTag.includes('flight')) return <FaPlane className='text-[10px]' />
  if (normalizedTag.includes('visa') || normalizedTag.includes('processing')) {
    return <FaPassport className='text-[10px]' />
  }
  if (normalizedTag.includes('limited') || normalizedTag.includes('slot')) {
    return <FaClock className='text-[10px]' />
  }
  return <FaTag className='text-[10px]' />
}

const buildSlugMap = (records: RefRecord[]): Map<string, string> => {
  const map = new Map<string, string>()
  records.forEach((r) => {
    if (r.id && r.slug) map.set(r.id, r.slug)
  })
  return map
}

const hasCountrySelection = (selection: CountrySelection) => {
  if (Array.isArray(selection)) {
    return selection.some((entry) => String(entry || '').trim().length > 0)
  }

  if (typeof selection === 'string') {
    return selection
      .split(/[,|;/]+/)
      .some((entry) => entry.trim().length > 0)
  }

  return false
}

const getCountrySelection = (record: {
  countries?: CountrySelection
  country?: string | null
  leadCountry?: string | null
  siteCountry?: string | null
  sourceCountry?: string | null
  market?: string | null
}): CountrySelection =>
  record.countries ||
  record.country ||
  record.leadCountry ||
  record.siteCountry ||
  record.sourceCountry ||
  record.market ||
  null

const April: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const siteRegion = import.meta.env.VITE_SITE_REGION || 'IN'
  const offerCurrency = siteRegion === 'AE' ? 'AED ' : '₹'

  const { data: featuredPicks, loading } = useCMS<CampaignPick[]>(() =>
    cmsApi.getFeaturedPicks({ campaignType: 'featured' })
  )
  const { data: destinations } = useCMS<RefRecord[]>(() =>
    cmsApi.getDestinations('isActive=true')
  )
  const { data: visaDestinations } = useCMS<RefRecord[]>(() =>
    cmsApi.getVisaDestinations()
  )

  if (loading) return <div className='py-20 text-center'>Loading...</div>

  const destinationSlugMap = buildSlugMap(
    (destinations || []).filter((destination) => {
      const selection = getCountrySelection(destination)
      return hasCountrySelection(selection) && isMatchingCountry(selection, siteRegion)
    }),
  )
  const visaSlugMap = buildSlugMap(
    (visaDestinations || []).filter((destination) => {
      const selection = getCountrySelection(destination)
      return hasCountrySelection(selection) && isMatchingCountry(selection, siteRegion)
    }),
  )

  const picks = (featuredPicks || [])
    .filter((pick) => {
      const selection = getCountrySelection(pick)
      return hasCountrySelection(selection) && isMatchingCountry(selection, siteRegion)
    })
    .filter(isFeaturedPick)
    .slice(0, 5)

  const resolveDestination = (pick: CampaignPick): string => {
    if (pick.ctaUrl) {
      try { return new URL(pick.ctaUrl).pathname } catch { return pick.ctaUrl }
    }
    const type = normalizeCampaignValue(pick.type || pick.category)
    if (type.includes('visa')) {
      const slug = pick.referenceId ? visaSlugMap.get(pick.referenceId) : undefined
      return slug ? `/visa-services/${slug}` : '/visa-services'
    }
    const slug = pick.referenceId ? destinationSlugMap.get(pick.referenceId) : undefined
    return slug ? `/destination-detail/${slug}` : '/destination'
  }

  const renderPickCard = (item: CampaignPick) => {
    const itemType = normalizeCampaignValue(item.type || item.category)
    const isPackage = itemType.includes('package')
    const isVisa = itemType.includes('visa')
    const originalPrice = formatPrice(item.originalPrice)
    const discountedPrice = formatPrice(item.discountedPrice)
    const expiresOn = formatExpiryDate(item.expiresOn)
    const highlights = item.highlights || []
    const tags =
      item.tags && item.tags.length > 0
        ? item.tags
        : isPackage
          ? []
          : isVisa
            ? []
            : []
    const destination = resolveDestination(item)

    return (
      <div
        key={item.id || item.slug || item.title || item.name}
        className='bg-white rounded-2xl overflow-hidden card-shadow border border-gray-100 hover-lift group flex flex-col h-full transition-all duration-300 hover:shadow-xl cursor-pointer'
        onClick={() => navigate(destination)}
      >
        <div className='relative h-56 overflow-hidden'>
          <img
            src={item.imageUrl}
            alt={item.title || item.name}
            className='w-full h-full object-cover group-hover:scale-105 transition-transform duration-500'
          />

          <div className='absolute top-4 left-4 bg-brand-dark/80 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-semibold text-white flex items-center gap-2'>
            {isPackage ? <FaPlane /> : <FaPassport />}
            <span>{isPackage ? t('Package') : isVisa ? t('Visa Service') : t('Offer')}</span>
          </div>

          {item.rating && (
            <div className='absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-brand-dark shadow-sm flex items-center gap-1'>
              <FaStar className='text-yellow-400' />
              {item.rating}
            </div>
          )}

          {item.badgeText && (
            <div className='absolute bottom-4 left-4 bg-brand-pink text-white px-3 py-1 rounded-md text-xs font-semibold uppercase tracking-wider'>
              {item.badgeText}
            </div>
          )}

          <div className='absolute bottom-4 right-4 bg-brand-dark/90 backdrop-blur-sm px-3 py-1.5 rounded-lg'>
            <div className='flex items-baseline gap-1'>
              {originalPrice && (
                <span className='text-gray-400 text-xs line-through'>
                  {offerCurrency}{originalPrice}
                </span>
              )}
              <span className='text-brand-pink font-bold text-lg'>
                {discountedPrice ? `${offerCurrency}${discountedPrice}` : t('Contact Us')}
              </span>
            </div>
          </div>
        </div>

        <div className='p-6 flex flex-col flex-grow'>
          <h3 className='font-heading text-xl font-bold text-brand-dark mb-1'>
            {item.title || item.name}
          </h3>
          <p className='text-brand-muted text-sm mb-3'>{item.subtitle}</p>

          <div className='flex items-center gap-3 text-sm text-brand-muted mb-4 flex-shrink-0 flex-wrap'>
            <span className='flex items-center gap-1'>
              <FaClock />
              {item.duration ? t(item.duration) : t('Limited Time')}
            </span>
            {expiresOn && (
              <span className='rounded-full bg-brand-gray px-2 py-1 text-xs text-brand-dark'>
                {t('Expires')} {expiresOn}
              </span>
            )}
          </div>

          {tags.length > 0 && (
            <div className='mb-3 flex flex-wrap gap-2'>
              {tags.slice(0, 3).map((tag, index) => (
                <span
                  key={`${tag}-${index}`}
                  className='inline-flex items-center gap-1.5 text-xs bg-brand-gray px-2 py-1 rounded-full text-brand-dark'
                >
                  {getTagIcon(tag)}
                  {t(tag)}
                </span>
              ))}
            </div>
          )}

          <div className='flex-grow overflow-auto mb-4'>
            <p className='text-sm text-gray-600 mb-4'>
              {item.description ? t(item.description) : ''}
            </p>

            {highlights.length > 0 && (
              <div className='flex flex-wrap gap-2'>
                {highlights.slice(0, 3).map((highlight, index) => (
                  <span
                    key={`${highlight}-${index}`}
                    className='text-xs bg-brand-gray px-2 py-1 rounded-full text-brand-dark'
                  >
                    {t(highlight)}
                  </span>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={() => navigate(destination)}
            className='w-full py-3 rounded-xl bg-brand-pink text-white font-medium hover:bg-brand-pink/90 transition-all duration-300 flex items-center justify-center gap-2 group'
          >
            <span>{item.buttonText || item.ctaText || t('Book Now')}</span>
            <FaArrowRight className='group-hover:translate-x-1 transition-transform' />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className='min-h-screen bg-white'>
      <section className='py-20 bg-brand-gray'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-center mb-8'>
            <div className='inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm rounded-full px-5 py-2 shadow-sm border border-brand-pink/20'>
              <span className='text-brand-pink font-bold text-sm'>🔥</span>
              <span className='text-brand-dark text-sm font-medium'>
                {t('Limited availability • Book by June 30')}
              </span>
            </div>
          </div>

          <div className='text-center mb-12'>
            <span className='text-brand-pink text-sm font-semibold tracking-widest uppercase'>
              {t('June Exclusive')}
            </span>
            <h2 className='font-heading text-3xl md:text-4xl font-bold text-brand-dark mt-2'>
              {t('Featured Hot Picks')}
            </h2>
            <p className='text-brand-muted max-w-2xl mx-auto mt-4'>
              {t('Handpicked destinations and visa services with special June discounts')}
            </p>
          </div>

          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8'>
            {picks.slice(0, 3).map((item) => renderPickCard(item))}
          </div>

          {picks.length > 3 && (
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-8 mt-8 lg:w-2/3 mx-auto'>
              {picks.slice(3, 5).map((item) => renderPickCard(item))}
            </div>
          )}
        </div>
      </section>

      <style>{`
        .hover-lift {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .hover-lift:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }
        .card-shadow {
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }
      `}</style>
    </div>
  )
}

export default April
